import React from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { PageIndicatorDots, PageContainer, colors } from "@/ui";
import BackButton from "@/ui/BackButton";
import ContinueButton from "@/components/Buttons/ContinueButton";
import MethodCard from "@/components/MethodExplanation/MethodCard";
import { useHideNavBar } from "@/context/NavVisibilityContext";
import listeningImg from "@/assets/listening.png";
import contentImg from "@/assets/content.png";
import clickImg from "@/assets/click.png";
import parrotSpeakingImg from "@/assets/parrot-speaking.png";
import calendarImg from "@/assets/calendar.png";

/**
 * MethodExplanationPage — "What is the Parrot method?" 5-card carousel.
 *
 * Mirrors iOS client-native/src/features/method-explanation/screens/
 * MethodExplanationScreen.tsx (METHOD_STEPS). Header title above the cards,
 * a swipeable horizontal carousel of 5 cards, tappable page-indicator dots,
 * and a bottom-pinned full-width button ("Next" on steps 1-4, the final label
 * on the last). On completion navigates to /placement.
 */
const METHOD_STEPS: { image: string; title: string; description: string }[] = [
  {
    image: listeningImg,
    title: "Listen First",
    description:
      "Watch videos on loop until you understand them. The more you listen, the faster you learn.",
  },
  {
    image: contentImg,
    title: "Real Content Only",
    description:
      "Learn from real videos, not textbook stuff. You'll learn how people actually speak Spanish.",
  },
  {
    image: clickImg,
    title: "Click & Save Words",
    description:
      "Tap any word to see what it means. Save words and phrases to review later.",
  },
  {
    image: parrotSpeakingImg,
    title: "Repeat Like a Parrot",
    description:
      "Say what you hear out loud while watching. Best way to start speaking naturally.",
  },
  {
    image: calendarImg,
    title: "Practice Daily",
    description:
      "Come back every day and watch your videos. Consistency = fluency.",
  },
];

const SWIPE_THRESHOLD = 50;

export interface MethodExplanationPageProps {
  finalButtonText?: string;
  onComplete?: () => void;
  /**
   * Show a back chevron at the top-left. The onboarding entry omits it (forward-
   * only flow); the Settings entry (iOS /settings/parrot-method, which pushes
   * this screen) shows it so the user can return to the hub.
   */
  showBackButton?: boolean;
  onBack?: () => void;
}

const MethodExplanationPage: React.FC<MethodExplanationPageProps> = ({
  finalButtonText = "Start Placement Test",
  onComplete,
  showBackButton = false,
  onBack,
}) => {
  const nav = useNavigate();
  const queryClient = useQueryClient();
  // Full-screen carousel: hide HomeLayout's bottom nav while mounted (matches
  // iOS, where this is a pushed route with no tab bar). No-op outside HomeLayout
  // (e.g. the onboarding /method-explanation route).
  useHideNavBar();
  const [step, setStep] = React.useState(0);
  const touchStartX = React.useRef<number | null>(null);
  const total = METHOD_STEPS.length;
  const isLast = step === total - 1;

  // iOS refetches user info on mount: the user may have just converted from a
  // temp to a full account, so downstream screens must read the fresh authID.
  React.useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["userInfo"] });
  }, [queryClient]);

  const goTo = (index: number) => {
    setStep(Math.max(0, Math.min(total - 1, index)));
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (delta <= -SWIPE_THRESHOLD) goTo(step + 1);
    else if (delta >= SWIPE_THRESHOLD) goTo(step - 1);
    touchStartX.current = null;
  };

  const handleNext = () => {
    if (isLast) {
      if (onComplete) onComplete();
      else nav("/placement");
      return;
    }
    goTo(step + 1);
  };

  return (
    <div
      className="relative flex w-full flex-col"
      style={
        // Settings entry: a fixed full-viewport overlay so it escapes the
        // profile/settings padding it's mounted inside (pt-3 pb-24 + an outer
        // PageContainer) — otherwise 100dvh + that padding overflows and leaves
        // dead scrollable whitespace below the button. Nav is already hidden
        // here, so covering the viewport is correct.
        showBackButton
          ? {
              position: "fixed",
              inset: 0,
              zIndex: 40,
              backgroundColor: "var(--color-bg)",
            }
          : { minHeight: "100dvh", backgroundColor: "var(--color-bg)" }
      }
    >
      <PageContainer
        padded={false}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
      >
        {showBackButton ? (
          // Settings entry: a compact header row — back chevron on the left with
          // the title inline and centered, matching the other settings
          // sub-screens (SettingsSubPage). Keeps the title next to the back
          // button instead of a giant heading floating below it.
          <div className="flex w-full items-center px-2 pt-3 pb-2">
            <BackButton onPress={() => onBack?.()} color={colors.primary} />
            {/* Larger title; wrapping to two lines is fine here. */}
            <span
              className="flex-1 text-center text-xl font-bold"
              style={{ color: colors.primary }}
            >
              What is the Parrot method?
            </span>
            {/* spacer to keep the title visually centered against the chevron */}
            <span style={{ width: 32 }} />
          </div>
        ) : (
          <div
            className="px-6 pb-4 text-center text-3xl font-extrabold text-[var(--color-text-primary)]"
            style={{ paddingTop: 32 }}
          >
            What is the Parrot method?
          </div>
        )}

        <div
          className="flex w-full min-w-0 max-w-full flex-1 flex-col justify-center overflow-hidden"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div
            className="flex w-full"
            style={{
              transform: `translateX(-${step * 100}%)`,
              transition: "transform 300ms ease",
            }}
          >
            {METHOD_STEPS.map((s, index) => (
              <MethodCard
                key={s.title}
                image={s.image}
                title={s.title}
                description={s.description}
                active={index === step}
              />
            ))}
          </div>
        </div>

        <div className="py-2">
          <PageIndicatorDots
            totalCount={total}
            currentIndex={step}
            size="small"
            expandActive={false}
            activeColor="var(--color-blue)"
            inactiveColor="var(--color-grey-light)"
            onDotPress={goTo}
          />
        </div>

        <div
          className={`px-4 pb-4 pt-4 ${
            showBackButton ? "" : "border-t border-[var(--color-grey-light)]"
          }`}
        >
          <ContinueButton
            text={isLast ? finalButtonText : "Next"}
            onClick={handleNext}
          />
        </div>
      </PageContainer>
    </div>
  );
};

export default MethodExplanationPage;
